import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { reelService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Reel {
  id: number;
  project_id: number;
  prompt: string;
  script?: string;
  language: string;
  voice?: string;
  voice_pitch?: number;
  voice_speed?: number;
  audio_path?: string;
  video_path?: string;
  thumbnail_path?: string;
  status: 'draft' | 'processing' | 'ready' | 'published' | 'failed' | 'scheduled';
  platforms_to_publish: string[];
  custom_caption?: string;
  custom_hashtags: string[];
  scheduled_time?: string;
  visual_source?: string;
  visual_config?: any;
  duration?: number;
  render_time?: number;
  created_at: string;
  published_at?: string;
  project_name?: string;
}

const Reels: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const [reels, setReels] = useState<Reel[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reelToDelete, setReelToDelete] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchReels();
  }, [page, rowsPerPage, statusFilter, projectFilter]);

  const fetchReels = async () => {
    try {
      setLoading(true);
      const params: any = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (projectFilter !== 'all') {
        params.project_id = projectFilter;
      }
      
      const response = await reelService.list(params);
      if (response.data && response.data.items) {
        setReels(response.data.items);
        setTotalCount(response.data.total || 0);
      } else if (Array.isArray(response.data)) {
        // Fallback for non-paginated response
        setReels(response.data);
        setTotalCount(response.data.length);
      } else {
        setReels([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch reels:', error);
      enqueueSnackbar('Failed to load reels', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReel = () => {
    navigate('/reels/create');
  };

  const handleViewReel = (id: number) => {
    navigate(`/reels/${id}`);
  };

  const handleEditReel = (id: number) => {
    navigate(`/reels/${id}/edit`);
  };

  const handleDeleteClick = (id: number) => {
    setReelToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reelToDelete) return;
    
    try {
      setProcessing(true);
      await reelService.delete(reelToDelete);
      enqueueSnackbar('Reel deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setReelToDelete(null);
      fetchReels();
    } catch (error) {
      console.error('Failed to delete reel:', error);
      enqueueSnackbar('Failed to delete reel', { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessReel = async (id: number) => {
    try {
      setProcessing(true);
      await reelService.process(id);
      enqueueSnackbar('Reel processing started', { variant: 'info' });
      fetchReels();
    } catch (error) {
      console.error('Failed to process reel:', error);
      enqueueSnackbar('Failed to start processing', { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadReel = async (id: number) => {
    try {
      const response = await reelService.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reel_${id}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download reel:', error);
      enqueueSnackbar('Failed to download reel', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
      case 'published':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      case 'draft':
      case 'scheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const filteredReels = reels && Array.isArray(reels) ? reels.filter(reel =>
    (reel.prompt?.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (reel.project_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (reel.script?.toLowerCase().includes(searchQuery.toLowerCase()))
  ) : [];

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3
        }}>
          <Typography variant="h4" component="h1">
            Reels
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 2,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchReels}
              disabled={loading}
              sx={{ flex: { xs: 1, sm: 'initial' } }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateReel}
              sx={{ flex: { xs: 1, sm: 'initial' } }}
            >
              Create New Reel
            </Button>
          </Box>
        </Box>

        <Paper sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search reels..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Project"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <MenuItem value="all">All Projects</MenuItem>
                {/* Projects will be loaded dynamically */}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : filteredReels.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No reels found
            </Typography>
            <Typography color="text.secondary" paragraph>
              {searchQuery || statusFilter !== 'all' || projectFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first reel to get started'}
            </Typography>
            {!searchQuery && statusFilter === 'all' && projectFilter === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateReel}
                sx={{ mt: 2 }}
              >
                Create First Reel
              </Button>
            )}
          </Paper>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,.2)',
                  borderRadius: 4,
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Thumbnail</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Project</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Language</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Duration</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReels.map((reel) => (
                    <TableRow key={reel.id} hover sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {reel.thumbnail_path ? (
                          <CardMedia
                            component="img"
                            sx={{ width: { xs: 60, sm: 80 }, height: { xs: 34, sm: 45 }, borderRadius: 1 }}
                            image={reel.thumbnail_path}
                            alt={reel.prompt}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: { xs: 60, sm: 80 },
                              height: { xs: 34, sm: 45 },
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PlayIcon color="disabled" />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {reel.prompt.length > 50 ? reel.prompt.substring(0, 50) + '...' : reel.prompt}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                          {reel.script ? (reel.script.length > 100 ? reel.script.substring(0, 100) + '...' : reel.script) : 'No script'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{reel.project_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(reel.status)}
                          color={getStatusColor(reel.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{reel.language.toUpperCase()}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{reel.duration}s</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {format(new Date(reel.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleViewReel(reel.id)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {reel.status === 'draft' && (
                          <Tooltip title="Process Video">
                            <IconButton
                              size="small"
                              onClick={() => handleProcessReel(reel.id)}
                              disabled={processing}
                            >
                              <PlayIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {reel.status === 'ready' && reel.video_path && (
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReel(reel.id)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(reel.id)}
                            disabled={processing}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Reel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this reel? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={processing}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Reels;